import { profileApi } from '@pukka/api';
import { Table } from '@pukka/ui-components';
import { OutletSelectorV2Modal, OutletTooltip } from '@pukka/ui-composite';
import { CellContext, Row } from '@tanstack/react-table';
import { format } from 'date-fns';
import { useSnapshot } from 'valtio';

import { AssignOutletCell } from './AssignOutletCell';
import { closeModal, DetailedCampaignWithStatus, DetailedCampaignWithStatusAndLoading, State } from './CustomerDisplay.state';
import MaxCampaignError from './MaxCampaignError';

const STATUS_CELL_ATTR = {
  scheduled: {
    label: 'Scheduled',
    className: 'text-yellow-400',
  },
  active: {
    label: 'Ongoing',
    className: 'text-green-500',
  },
  inactive: {
    label: 'Inactive',
    className: 'text-red-500',
  },
};

function ContentsCell({ images }: DetailedCampaignWithStatus) {
  return <span>{images.map((image) => image.name).join(', ')}</span>;
}

function TimePeriodCell({ end_date: endDate, start_date: startDate }: DetailedCampaignWithStatus) {
  if (endDate) {
    const formattedStartDate = format(new Date(startDate), 'dd MMM yyyy');
    const formattedEndDate = format(new Date(endDate), 'dd MMM yyyy');

    return <span>{`${formattedStartDate} - ${formattedEndDate}`}</span>;
  }

  return <span>-</span>;
}

function StatusCell({ status }: DetailedCampaignWithStatus) {
  const { className, label } = STATUS_CELL_ATTR[status];

  return <span className={className}>{label}</span>;
}

function OutletCell({ outlet_ids: outletIds }: DetailedCampaignWithStatus) {
  const { data: profile } = profileApi.useProfile();

  const outlets = outletIds.map((outletId) => {
    return profile?.outlets?.find((o) => o.id === outletId) || {};
  });

  const totalOutlets = outletIds.length;

  if (totalOutlets < 2) {
    return <span>{outlets[0]?.name || '-'}</span>;
  }

  return (
    <OutletTooltip outlets={outlets} totalOutlets={totalOutlets}>
      <div className="inline rounded bg-light-blue-500 px-3 py-1.5 text-sm text-white">{totalOutlets} Outlets</div>
    </OutletTooltip>
  );
}

function Cell({ row, column }: CellContext<DetailedCampaignWithStatusAndLoading, unknown>) {
  const { isLoading, ...props } = row.original;

  if (isLoading) {
    return (
      <div data-testid="skeleton" className="m-auto">
        <span className="inline-block h-[15px] w-[100px] bg-neutral-200" />
      </div>
    );
  }

  const _props = props as DetailedCampaignWithStatus;

  switch (column.id) {
    case 'images':
      return <ContentsCell {..._props} />;
    case 'period':
      return <TimePeriodCell {..._props} />;
    case 'status':
      return <StatusCell {..._props} />;
    case 'outlet_ids':
      return <OutletCell {..._props} />;
    case 'assignOutlet':
      return <AssignOutletCell {..._props} />;
    case 'name':
    default:
      return row.original.name;
  }
}

const columns = [
  {
    header: 'Campaign Name',
    accessorKey: 'name',
    minSize: 120,
    cell: Cell,
  },
  {
    header: 'Campaign Contents',
    accessorKey: 'images',
    minSize: 170,
    cell: Cell,
  },
  {
    header: 'Time Period',
    accessorKey: 'period',
    minSize: 120,
    cell: Cell,
  },
  {
    header: 'Status',
    accessorKey: 'status',
    minSize: 120,
    cell: Cell,
  },
  {
    header: 'Outlet',
    accessorKey: 'outlet_ids',
    minSize: 120,
    cell: Cell,
  },
  {
    header: '',
    accessorKey: 'assignOutlet',
    minSize: 120,
    cell: Cell,
  },
];

const minimizedColumn = columns.filter((col) => col.header !== 'Outlet' && col.header !== 'Time Period');

interface CustomerDisplayTableProps {
  readonly data: DetailedCampaignWithStatusAndLoading[];
  readonly isMinimized?: boolean;
  readonly highlightedRowIndexes?: number[];
  readonly onRowClick?: (row: Row<DetailedCampaignWithStatusAndLoading>) => void;
  readonly onBottomReached?: (parentElement: HTMLTableSectionElement) => void;
}

function CustomerDisplayTable({ data, isMinimized, highlightedRowIndexes, onRowClick, onBottomReached }: CustomerDisplayTableProps) {
  const { modalData } = useSnapshot(State);
  const tableColumns = isMinimized ? minimizedColumn : columns;

  return (
    <>
      {modalData !== undefined ? (
        <OutletSelectorV2Modal
          open
          selectedIds={modalData.outletIds}
          canSelectAll
          minItems={1}
          errorMessage={modalData.errorOutlets.length > 0 ? <MaxCampaignError outlets={modalData.mappedErrorOutlets as string[]} /> : ''}
          onSubmit={modalData.handleChangeOutlets}
          onClickCancel={closeModal}
        />
      ) : null}
      <Table
        data={data}
        columns={tableColumns}
        bordered
        rowOnClick={onRowClick}
        highlightedRowIndexes={highlightedRowIndexes}
        onBottomReached={onBottomReached}
      />
    </>
  );
}

export { CustomerDisplayTable };
